import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Map,
  ShoppingBag,
  Wallet,
  Camera,
  AlertCircle,
  Menu,
  ChevronRight
} from 'lucide-react';
import { Show, SignInButton, SignUpButton, UserButton } from "@clerk/react";

// Views
import HomeView from './views/HomeView';
import BookingsView from './views/BookingsView';
import ExploreView from './views/ExploreView';
import MarketplaceView from './views/MarketplaceView';
import WalletView from './views/WalletView';
import MomentsView from './views/MomentsView';
import EmergencyView from './views/EmergencyView';
import AdminView from './views/AdminView';
import OnboardingView from './views/OnboardingView';
import ProviderOnboarding from './views/ProviderOnboarding';
import KYCView from './views/KYCView';
import { AppProvider, useApp } from './context/AppContext';

const BottomNav = () => {
  const location = useLocation();
  const navItems = [
    { path: '/', label: 'Home', icon: <Home size={20} /> },
    { path: '/bookings', label: 'Bookings', icon: <BookOpen size={20} /> },
    { path: '/explore', label: 'Explore', icon: <Map size={20} /> },
    { path: '/marketplace', label: 'Concierge', icon: <ShoppingBag size={20} /> },
    { path: '/wallet', label: 'Wallet', icon: <Wallet size={20} /> },
    { path: '/moments', label: 'Moments', icon: <Camera size={20} /> },
  ];

  return (
    <nav className="bottom-nav">
      {navItems.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          className={`nav-item \${location.pathname === item.path ? 'active' : ''}`}
        >
          {item.icon}
          <span>{item.label}</span>
        </Link>
      ))}
    </nav>
  );
};

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const Header = () => (
  <header className="main-header">
    <div className="logo-section">
      <span className="logo-text">EZ <span className="logo-accent">Naija</span></span>
    </div>
    <div className="header-actions">
      {PUBLISHABLE_KEY && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Show when="signed-out">
            <SignInButton mode="modal">
              <button style={{ 
                background: 'transparent', 
                color: 'var(--primary-green)', 
                border: '1px solid var(--primary-green)', 
                padding: '8px 16px', 
                borderRadius: '20px', 
                fontSize: '13px', 
                fontWeight: '700',
                cursor: 'pointer'
              }}>Log In</button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button style={{ 
                background: 'var(--primary-green)', 
                color: 'white', 
                border: 'none', 
                padding: '8px 16px', 
                borderRadius: '20px', 
                fontSize: '13px', 
                fontWeight: '700',
                cursor: 'pointer'
              }}>Join</button>
            </SignUpButton>
          </Show>
          <Show when="signed-in">
            <UserButton afterSignOutUrl="/" />
          </Show>
        </div>
      )}
      <Link to="/emergency" className="emergency-indicator">
        <AlertCircle size={24} color="var(--emergency-red)" />
      </Link>
      <Link to="/admin" className="admin-trigger">
        <Menu size={24} />
      </Link>
    </div>
  </header>
);

function AppContent() {
  const { user, setUser } = useApp();

  return (
    <div className="app-container">
      {!user.onboarded && (
        <OnboardingView 
          onComplete={() => {
            localStorage.setItem('ez_onboarded', 'true');
            setUser(prev => ({ ...prev, onboarded: true }));
          }} 
        />
      )}
      <Header />
      <main className="view-content">
        <Routes>
          <Route path="/" element={<HomeView />} />
          <Route path="/bookings" element={<BookingsView />} />
          <Route path="/explore" element={<ExploreView />} />
          <Route path="/marketplace" element={<MarketplaceView />} />
          <Route path="/wallet" element={<WalletView />} />
          <Route path="/moments" element={<MomentsView />} />
          <Route path="/emergency" element={<EmergencyView />} />
          <Route path="/admin" element={<AdminView />} />
          <Route path="/provider-setup" element={<ProviderOnboarding />} />
          <Route path="/kyc" element={<KYCView />} />
        </Routes>
      </main>
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppProvider>
        <AppContent />
        <style dangerouslySetInnerHTML={{
          __html: `
        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 100%;
          max-width: 480px;
          height: 70px;
          background: white;
          border-top: 1px solid var(--border-gray);
          display: flex;
          justify-content: space-around;
          align-items: center;
          padding: 0 10px;
          z-index: 1000;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
        }
        .nav-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-decoration: none;
          color: var(--text-light);
          font-size: 10px;
          gap: 4px;
          transition: 0.2s;
        }
        .nav-item.active {
          color: var(--primary-green);
        }
        .nav-item.active svg {
          transform: translateY(-2px);
          stroke-width: 2.5px;
        }
        .main-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px 20px;
          background: white;
          position: sticky;
          top: 0;
          z-index: 100;
          border-bottom: 1px solid var(--border-gray);
        }
        .logo-text {
          font-size: 20px;
          font-weight: 800;
          color: var(--primary-green);
        }
        .logo-accent {
          color: var(--text-dark);
        }
        .header-actions {
          display: flex;
          gap: 15px;
          align-items: center;
        }
        .emergency-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}} />
      </AppProvider>
    </Router>
  );
}

export default App;
